import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Megaphone, Send, UserX } from 'lucide-react'

import type { MarketingSegment, SegmentPreviewResult } from './api/types'
import { Badge } from './components/ui/badge'
import { Button } from './components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog'
import { Label } from './components/ui/label'
import { Textarea } from './components/ui/input'

export function MarketingSection({
  segments,
  onSendWinback,
}: {
  segments: MarketingSegment[] | null
  onSendWinback: (days: number) => void
}) {
  const { t } = useTranslation()
  const lapsed = segments?.find((s) => s.key === 'lapsed')
  const seasonal = segments?.find((s) => s.key === 'seasonal')
  const lapsedCount = lapsed?.count ?? 0
  const seasonalCount = seasonal?.count ?? 0

  return (
    <section className="mt-7 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold tracking-tight">{t('marketing.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('marketing.subtitle')}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div className="space-y-0.5">
              <CardTitle className="flex items-center gap-2">
                <UserX className="size-4 text-muted-foreground" />
                {t('marketing.lapsedTitle')}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {t('marketing.lapsedHook', { count: lapsedCount })}
              </p>
            </div>
            <Badge>{lapsedCount}</Badge>
          </CardHeader>
          <CardContent className="pt-0">
            <Button
              className="w-full"
              disabled={lapsedCount === 0}
              onClick={() => onSendWinback(60)}
            >
              <Send className="size-4" />
              {t('marketing.lapsedCta')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div className="space-y-0.5">
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="size-4 text-muted-foreground" />
                {t('marketing.seasonalTitle')}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {t('marketing.seasonalHook', { count: seasonalCount })}
              </p>
            </div>
            <Badge variant="secondary">{seasonalCount}</Badge>
          </CardHeader>
          <CardContent className="pt-0">
            <Button variant="outline" className="w-full" disabled>
              {t('marketing.comingSoon')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}

export function SendWinbackDialog({
  open,
  preview,
  busy,
  onCancel,
  onSend,
}: {
  open: boolean
  preview: SegmentPreviewResult | null
  busy: boolean
  onCancel: () => void
  onSend: (messageText: string, days: number) => void
}) {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (open) {
      setMessage((m) =>
        m.trim() ? m : t('marketing.defaultMessage', { ownerName: '林小姐', petName: '毛毛' }),
      )
    }
  }, [open, t])

  const days = (preview?.filters?.days_inactive as number | undefined) ?? 60
  const count = preview?.count ?? 0
  const ready = !!preview && !busy && message.trim().length >= 8

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('marketing.confirmTitle')}</DialogTitle>
          <DialogDescription>
            {preview ? t('marketing.confirmRecipients', { count }) : t('loading')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="msg">{t('marketing.confirmMessageLabel')}</Label>
          <Textarea
            id="msg"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        {preview && preview.sample.length > 0 && (
          <div className="space-y-1.5">
            <Label>{t('marketing.confirmSampleLabel')}</Label>
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
              {preview.sample.map((o) => (
                <li key={o.id}>{o.name}</li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t('marketing.cancel')}
          </Button>
          <Button onClick={() => onSend(message.trim(), days)} disabled={!ready}>
            {busy ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('marketing.sending')}
              </>
            ) : (
              <>
                <Send className="size-4" />
                {t('marketing.send')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
